import { organisationPermissionDefaultValue, ReadFeatureMetricsPermission } from '../constants'
import { organisationPermissions } from '../constants'
import { organisationPermissionsData } from '../constants'
import { allPermissions } from '../constants'
import {
	AdminPermission,
	ReadPermissionsPermission,
	ReadBetaAccessPermission,
	permissionsInheritanceMap,
	ReadSubscriptionMetricsPermission,
	systemPermissions,
	systemTranslationLanguageCodes,
	ReadSubscriptionsPermission,
	WriteBetaAccessPermission,
	WritePermissionsPermission,
	WriteSubscriptionsPermission,
	WriteTranslationPermissionsPermission,
	ReadTranslationsPermission,
	userPermissions,
	writeTranslationsPermissions,
	userPermissionsData,
	WriteTranslationsPermissionKey,
	WriteTranslationsPermissionPrefix
} from '../constants'
import { DBPermissionsType, PermissionsType, SystemPermissionsType, SystemTranslationLanguageCode } from '../interfaces'
import { OrganisationPermissionsKeys } from '../interfaces'
import { PermissionsKeys } from '../interfaces'
import { UserPermissionsKeys } from '../interfaces'
import { getOrganisationAvatarLink } from './avatarUtils'

export const hasUserPermission = (targetPermissions: PermissionsKeys | PermissionsKeys[], userPermissions: PermissionsType): boolean => {
	const normalisedPermissions = normalisePermissions(userPermissions)
	if (!targetPermissions.length) return true
	const targetPermissionsArray = Array.isArray(targetPermissions) ? targetPermissions : [targetPermissions]
	return targetPermissionsArray.some((permission) => {
		const mappedPermissions = isSystemPermission(permission) ? permissionsInheritanceMap[permission] : undefined
		return (
			getPermissionValue(normalisedPermissions, permission) ||
			Boolean(mappedPermissions?.some((mappedPermission) => getPermissionValue(normalisedPermissions, mappedPermission)))
		)
	})
}

export const getUserPermissionFilterKeys = (
	permissionKey: UserPermissionsKeys,
	viewablePermissions?: Set<UserPermissionsKeys>
): UserPermissionsKeys[] => {
	if (!viewablePermissions) return [permissionKey]
	if (!viewablePermissions.has(permissionKey)) return []
	return [permissionKey, ...((permissionsInheritanceMap[permissionKey] ?? []) as UserPermissionsKeys[])]
}

export type UserPermissionManagementAction = 'grant' | 'revoke'

export const canManageUserPermission = ({
	profilePermissions,
	targetPermission,
	profileUserId,
	targetUserId,
	action = 'grant'
}: {
	profilePermissions: PermissionsType
	targetPermission: UserPermissionsKeys
	profileUserId?: string
	targetUserId?: string
	action?: UserPermissionManagementAction
}): boolean => {
	const isSelfAdminRevoke =
		action === 'revoke' && targetPermission === AdminPermission && Boolean(profileUserId) && Boolean(targetUserId) && profileUserId === targetUserId
	if (isSelfAdminRevoke) return false

	if (hasUserPermission(AdminPermission, profilePermissions)) return true
	if (targetPermission === AdminPermission || targetPermission === WritePermissionsPermission) return false
	if (targetPermission === ReadBetaAccessPermission && hasUserPermission(WriteBetaAccessPermission, profilePermissions)) return true
	if (targetPermission === ReadTranslationsPermission && hasUserPermission(WriteTranslationPermissionsPermission, profilePermissions)) return true
	if (isWriteTranslationsPermission(targetPermission) && hasUserPermission(WriteTranslationPermissionsPermission, profilePermissions)) return true
	if (hasUserPermission(WritePermissionsPermission, profilePermissions) && hasUserPermission(targetPermission, profilePermissions)) return true
	return false
}

export const getViewableUserPermissions = (profilePermissions: PermissionsType): Set<UserPermissionsKeys> => {
	const normalisedPermissions = normalisePermissions(profilePermissions)
	if (
		getPermissionValue(normalisedPermissions, AdminPermission) ||
		getPermissionValue(normalisedPermissions, ReadPermissionsPermission)
	) {
		return new Set(userPermissions)
	}

	const viewable = new Set<UserPermissionsKeys>()

	if (getPermissionValue(normalisedPermissions, WriteBetaAccessPermission)) {
		viewable.add(ReadBetaAccessPermission)
	}

	if (getPermissionValue(normalisedPermissions, WriteTranslationPermissionsPermission)) {
		viewable.add(ReadTranslationsPermission)
		writeTranslationsPermissions.forEach((permission) => viewable.add(permission))
	}

	if (getPermissionValue(normalisedPermissions, WritePermissionsPermission)) {
		userPermissions.forEach((permission) => {
			if (canManageUserPermission({ profilePermissions, targetPermission: permission, action: 'grant' })) {
				viewable.add(permission)
			}
		})
	}

	return viewable
}

const permissionIdToPermissionKey = (permissionId: number): PermissionsKeys => {
	const permission = allPermissions.find((permission) => getPermissionData(permission).permissionId === permissionId)
	if (!permission) throw new Error(`PermissionId ${permissionId} not found`)
	return permission
}

export const userPermissionKeyToPermissionId = (permissionKey: UserPermissionsKeys): number => {
	const permissionData = userPermissionsData[permissionKey]
	if (!permissionData) throw new Error(`User PermissionKey ${permissionKey} not found`)
	return permissionData.permissionId
}

export const organisationPermissionKeyToPermissionId = (permissionKey: OrganisationPermissionsKeys): number => {
	const permissionData = organisationPermissionsData[permissionKey]
	if (!permissionData) throw new Error(`Organisation PermissionKey ${permissionKey} not found`)
	return permissionData.permissionId
}

const isSystemPermission = (permission: PermissionsKeys): permission is UserPermissionsKeys => permission in userPermissionsData
const systemBooleanPermissionData = Object.fromEntries(systemPermissions.map((permission) => [permission, true]))
const isSystemBooleanPermission = (permission: PermissionsKeys): permission is (typeof systemPermissions)[number] => permission in systemBooleanPermissionData

const getPermissionData = (permission: PermissionsKeys): { defaultValue: boolean | typeof organisationPermissionDefaultValue; permissionId: number } =>
	isSystemPermission(permission) ? userPermissionsData[permission] : organisationPermissionsData[permission]

const isWriteTranslationsPermission = (permission: PermissionsKeys): permission is WriteTranslationsPermissionKey =>
	permission.startsWith(WriteTranslationsPermissionPrefix)

const isSystemTranslationLanguageCode = (languageCode: unknown): languageCode is SystemTranslationLanguageCode =>
	typeof languageCode === 'string' && systemTranslationLanguageCodes.some((systemTranslationLanguageCode) => systemTranslationLanguageCode === languageCode)

const getWriteTranslationLanguageCode = (permission: WriteTranslationsPermissionKey): SystemTranslationLanguageCode => {
	const languageCode = permission.replace(WriteTranslationsPermissionPrefix, '')
	if (!isSystemTranslationLanguageCode(languageCode)) throw new Error(`Translation language code ${languageCode} not found`)
	return languageCode
}

const isOrganisationPermissionValue = (permissionValue: unknown): permissionValue is typeof organisationPermissionDefaultValue =>
	typeof permissionValue === 'object' && permissionValue !== null && 'isGranted' in permissionValue && typeof permissionValue.isGranted === 'boolean'

export const getPermissionValue = (permissions: PermissionsType, permission: PermissionsKeys): boolean => {
	const normalisedPermissions = normalisePermissions(permissions)
	const system = normalisedPermissions.system ?? getDefaultSystemPermissions()
	if (isWriteTranslationsPermission(permission)) {
		return system.writeTranslations?.includes(getWriteTranslationLanguageCode(permission)) ?? false
	}
	if (isSystemBooleanPermission(permission)) return system[permission] ?? false
	return normalisedPermissions[permission].isGranted
}

const setPermissionValue = (
	permissions: PermissionsType,
	permission: PermissionsKeys,
	value: boolean | typeof organisationPermissionDefaultValue
): PermissionsType => {
	const isGranted = typeof value === 'boolean' ? value : value.isGranted
	const system = { ...getDefaultSystemPermissions(), ...permissions.system }
	if (isWriteTranslationsPermission(permission)) {
		const languageCode = getWriteTranslationLanguageCode(permission)
		const writeTranslations = isGranted
			? system.writeTranslations.includes(languageCode)
				? system.writeTranslations
				: [...system.writeTranslations, languageCode]
			: system.writeTranslations.filter((permissionLanguageCode) => permissionLanguageCode !== languageCode)
		return { ...permissions, system: { ...system, writeTranslations } }
	}
	if (isSystemPermission(permission)) return { ...permissions, system: { ...system, [permission]: isGranted } }
	return { ...permissions, [permission]: typeof value === 'boolean' ? { isGranted: value } : value }
}

export const getDefaultOrganisationPermissions = (perms: PermissionsKeys[] = [...organisationPermissions]): PermissionsType =>
	perms.reduce(
		(acc, permission) => {
			if (!isSystemPermission(permission)) return { ...acc, [permission]: organisationPermissionsData[permission].defaultValue }
			return acc
		},
		{
			organisationActivities: organisationPermissionsData.organisationActivities.defaultValue,
			organisationAi: organisationPermissionsData.organisationAi.defaultValue,
			organisationConversions: organisationPermissionsData.organisationConversions.defaultValue,
			organisationDrive: organisationPermissionsData.organisationDrive.defaultValue,
			organisationSharing: organisationPermissionsData.organisationSharing.defaultValue
		}
	)

export const getDefaultSystemPermissions = (): SystemPermissionsType => ({
	...systemPermissions.reduce((acc, permission) => ({ ...acc, [permission]: userPermissionsData[permission].defaultValue }), {} as SystemPermissionsType),
	writeTranslations: []
})

export const getDefaultUserPermissions = (perms: PermissionsKeys[] = [...allPermissions]): PermissionsType => {
	const defaultPermissions: PermissionsType = {
		...getDefaultOrganisationPermissions(),
		system: getDefaultSystemPermissions()
	}

	return perms.reduce((acc, permission) => setPermissionValue(acc, permission, getPermissionData(permission).defaultValue), defaultPermissions)
}

const legacySystemPermissionKeys: Record<string, (typeof systemPermissions)[number]> = {
	admin: AdminPermission,
	translation: ReadTranslationsPermission,
	sahara: ReadFeatureMetricsPermission,
	beta: ReadBetaAccessPermission,
	subscriptionsAdminWrite: WriteSubscriptionsPermission,
	subscriptionsAdminRead: ReadSubscriptionsPermission,
	reports: ReadSubscriptionMetricsPermission
}

const getLegacyPermissionValue = (permissionValue: unknown): boolean | null => {
	if (typeof permissionValue === 'boolean') return permissionValue
	if (typeof permissionValue === 'object' && permissionValue && 'isGranted' in permissionValue && typeof permissionValue.isGranted === 'boolean') {
		return permissionValue.isGranted
	}
	return null
}

export const normalisePermissions = (permissions: PermissionsType): PermissionsType => {
	const sourceRecord = permissions as unknown as Record<string, unknown>
	const permissionsRecord =
		typeof sourceRecord.permissions === 'object' && sourceRecord.permissions ? (sourceRecord.permissions as Record<string, unknown>) : sourceRecord
	const sourceSystemRecord =
		typeof permissionsRecord.system === 'object' && permissionsRecord.system ? (permissionsRecord.system as Record<string, unknown>) : null
	let normalisedPermissions = getDefaultUserPermissions()

	organisationPermissions.forEach((permission) => {
		const permissionValue = permissionsRecord[permission]
		if (isOrganisationPermissionValue(permissionValue)) {
			normalisedPermissions = setPermissionValue(normalisedPermissions, permission, permissionValue)
		}
	})

	if (sourceSystemRecord) {
		const systemRecord = sourceSystemRecord
		systemPermissions.forEach((permission) => {
			const permissionValue = getLegacyPermissionValue(systemRecord[permission])
			if (permissionValue !== null) normalisedPermissions = setPermissionValue(normalisedPermissions, permission, permissionValue)
		})

		const writeTranslations = systemRecord.writeTranslations
		if (Array.isArray(writeTranslations)) {
			normalisedPermissions = {
				...normalisedPermissions,
				system: {
					...getDefaultSystemPermissions(),
					...normalisedPermissions.system,
					writeTranslations: writeTranslations.filter(isSystemTranslationLanguageCode)
				}
			}
		}
	}

	Object.entries(legacySystemPermissionKeys).forEach(([legacyPermission, systemPermission]) => {
		const permissionValue = getLegacyPermissionValue(permissionsRecord[legacyPermission])
		if (permissionValue !== null) normalisedPermissions = setPermissionValue(normalisedPermissions, systemPermission, permissionValue)
	})

	return normalisedPermissions
}

export const getSparseUserPermissions = (permissions: PermissionsType): PermissionsType => {
	const normalisedPermissions = normalisePermissions(permissions)
	const normalisedSystem = normalisedPermissions.system ?? getDefaultSystemPermissions()
	const system = systemPermissions.reduce<Partial<SystemPermissionsType>>((acc, permission) => {
		if (normalisedSystem[permission]) return { ...acc, [permission]: true }
		return acc
	}, {})

	if (normalisedSystem.writeTranslations?.length) {
		system.writeTranslations = normalisedSystem.writeTranslations
	}

	const { system: _system, ...organisationPermissions } = normalisedPermissions
	return Object.keys(system).length ? { ...organisationPermissions, system } : organisationPermissions
}

export const mapPermissionsToStructuredObject = (
	perms: PermissionsKeys[],
	permissions: DBPermissionsType[],
	onError: (error: unknown) => void,
	baseUrl: string
): PermissionsType => {
	const defaultPermissionsClone = getDefaultOrganisationPermissions(perms)
	const defaultPermissions = getDefaultOrganisationPermissions(perms)
	permissions.forEach(({ permissionTypeId, isGranted, organisationId, organisationName, organisationCountry }) => {
		try {
			const key = permissionIdToPermissionKey(permissionTypeId)
			const defaultIsGranted = getPermissionValue(defaultPermissionsClone, key)

			if (isGranted !== defaultIsGranted) {
				const value: typeof organisationPermissionDefaultValue = {
					isGranted
				}
				if (organisationId && organisationName) {
					value.organisationId = organisationId
					value.organisationName = organisationName
					if (organisationCountry) value.organisationCountry = organisationCountry
					value.organisationAvatarUrl = getOrganisationAvatarLink(baseUrl, String(organisationId))
				}
				if (!isSystemPermission(key)) Object.assign(defaultPermissions, { [key]: value })
			}
		} catch (e) {
			onError(e)
		}
	})
	return defaultPermissions
}

export const mapPermissionsToUserRootObject = (
	perms: PermissionsKeys[],
	permissions: DBPermissionsType[],
	onError: (error: unknown) => void,
	baseUrl: string
): PermissionsType => {
	const defaultPermissionsClone = getDefaultUserPermissions(perms)
	let defaultPermissions = getDefaultUserPermissions(perms)
	permissions.forEach(({ permissionTypeId, isGranted, organisationId, organisationName, organisationCountry }) => {
		try {
			const key = permissionIdToPermissionKey(permissionTypeId)
			const defaultIsGranted = getPermissionValue(defaultPermissionsClone, key)

			if (isGranted !== defaultIsGranted) {
				const value: typeof organisationPermissionDefaultValue = {
					isGranted
				}
				if (organisationId && organisationName) {
					value.organisationId = organisationId
					value.organisationName = organisationName
					if (organisationCountry) value.organisationCountry = organisationCountry
					value.organisationAvatarUrl = getOrganisationAvatarLink(baseUrl, String(organisationId))
				}
				defaultPermissions = setPermissionValue(defaultPermissions, key, value)
			}
		} catch (e) {
			onError(e)
		}
	})
	return getSparseUserPermissions(defaultPermissions)
}
